import { useEffect, useState } from "react";
import {
  useAppDesignPdf,
  useAssignedClientSelection,
  useFetch,
  useRequest,
} from "../../../hooks";
import { WellProps } from "../../../interfaces/interfaces";
import { DocRow, DocumentsPortalLayout } from "../../../components/documents/DocumentsPortalLayout";

interface WellsFetchResponse {
  success?: boolean;
  data: WellProps[];
}

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const latestUploadByProposalId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    const pid = row.proposalId;
    if (!pid) return;
    const cur = map.get(pid);
    if (!cur || new Date(row.date).getTime() > new Date(cur.date).getTime()) {
      map.set(pid, row);
    }
  });
  return map;
};

export const ProposalClient = () => {
  const { handleRequest } = useRequest();
  const { downloadProposalPdf } = useAppDesignPdf();
  const {
    clientOptions,
    selectedClientId,
    setSelectedClientId,
    showClientFilter,
  } = useAssignedClientSelection();

  const { data: wellsResp, isLoading: wellsLoading } =
    useFetch<WellsFetchResponse>(
      selectedClientId ? `well/client/${selectedClientId}` : undefined,
    );
  const wells: WellProps[] = wellsResp?.data ?? [];

  const [rows, setRows] = useState<DocRow[]>([]);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);

  const getWellName = (idWell?: number) =>
    wells.find((w) => w.id === idWell)?.name ?? "";

  useEffect(() => {
    if (!selectedClientId) return;
    setSelectedWellId(null);
    setRowsLoading(true);
    const opts: RequestInit = { method: "GET" };
    Promise.all([
      handleRequest({ endpoint: `proposal-list/client/${selectedClientId}`, options: opts }),
      handleRequest({ endpoint: `proposal-detail?client=${selectedClientId}`, options: opts }),
    ])
      .then(([uploadedRes, appRes]) => {
        const appRows = getRows(appRes);
        const appById = new Map<number, any>(
          appRows.map((item: any) => [item.id, item]),
        );
        const uploaded: DocRow[] = (
          isSuccessful(uploadedRes) ? uploadedRes.data : []
        ).map((item: any) => {
          const linkedProposal = appById.get(item.proposalId);
          return {
            rowId: `u-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.idWell,
            wellName: "",
            source: "uploaded" as const,
            path: item.path,
            owner: linkedProposal?.createdBy ?? "—",
            proposalId: item.proposalId,
          };
        });
        const uploadsByProposal = latestUploadByProposalId(uploaded);
        const app: DocRow[] = appRows.map((item: any) => {
          const def = uploadsByProposal.get(item.id);
          return {
            rowId: `a-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            wellId: item.well?.id,
            wellName: item.well?.name ?? "",
            source: "app" as const,
            appId: item.id,
            owner: item.createdBy ?? "—",
            defaultUploadPath: def?.path,
            defaultUploadName: def?.customName,
          };
        }).filter((row: DocRow) => row.appId == null || !uploadsByProposal.has(row.appId));
        const sorted = [...uploaded, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
      })
      .catch(() => setIsError(true))
      .finally(() => setRowsLoading(false));
  }, [selectedClientId]);

  useEffect(() => {
    if (!wells.length || !rows.length) return;
    setRows((prev) =>
      prev.map((r) => (r.wellName ? r : { ...r, wellName: getWellName(r.wellId) })),
    );
  }, [wells]);

  const downloadUploaded = (path: string, name: string): Promise<void> =>
    new Promise((resolve, reject) => {
      const opts: RequestInit = {
        method: "POST",
        body: JSON.stringify({ key: path }),
        headers: { responseType: "Buffer" } as any,
      };
      handleRequest({ endpoint: "pdf-proposal-adm", options: opts })
        .then((res) => {
          if (!res.success) { reject(new Error("Failed")); return; }
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", name.endsWith(".pdf") ? name : `${name}.pdf`);
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          resolve();
        })
        .catch(reject);
    });

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      await downloadUploaded(row.path, row.customName);
    } else if (row.source === "app" && row.defaultUploadPath) {
      await downloadUploaded(row.defaultUploadPath, row.defaultUploadName ?? row.customName);
    } else if (row.source === "app" && row.appId != null) {
      await downloadProposalPdf(row.appId, row.customName);
    }
  };

  return (
    <DocumentsPortalLayout
      title="Proposals"
      subtitle="Browse, filter and download client proposals"
      wells={wells}
      wellsLoading={wellsLoading}
      rows={rows}
      rowsLoading={rowsLoading}
      selectedWellId={selectedWellId}
      onWellSelect={setSelectedWellId}
      onDownload={handleDownload}
      showClientFilter={showClientFilter}
      selectedClientId={selectedClientId}
      clientOptions={clientOptions}
      onClientChange={(client) => setSelectedClientId(client?.id ?? null)}
      isError={isError}
    />
  );
};
